import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

interface BlockRow {
  id: string;
  task_class: string;
  provider: string;
  reason: string | null;
  created_at: string;
}

const TASK_CLASSES = [
  "fast", "balanced", "reasoning", "verifier", "multimodal", "retrieval",
  "factual_critical", "price_quote", "policy_statement", "procedural",
  "ltm_dependent", "date_time_statement",
];
const PROVIDERS = ["google", "openai", "anthropic", "lovable_gateway"];

export function BlockListEditor() {
  const qc = useQueryClient();
  const [taskClass, setTaskClass] = useState("");
  const [provider, setProvider] = useState("");
  const [reason, setReason] = useState("");

  const { data: blocks = [], isLoading } = useQuery({
    queryKey: ["model-block-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("model_block_list")
        .select("id, task_class, provider, reason, created_at")
        .order("task_class")
        .order("provider");
      if (error) throw error;
      return data as BlockRow[];
    },
  });

  const addBlock = useMutation({
    mutationFn: async () => {
      if (!taskClass || !provider || reason.trim().length < 5) {
        throw new Error("Preencha task_class, provider e motivo (≥5 caracteres).");
      }
      const { error } = await supabase.from("model_block_list").insert({
        task_class: taskClass,
        provider: provider,
        reason: reason.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Bloqueio adicionado.");
      setTaskClass("");
      setProvider("");
      setReason("");
      qc.invalidateQueries({ queryKey: ["model-block-list"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeBlock = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("model_block_list").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Bloqueio removido.");
      qc.invalidateQueries({ queryKey: ["model-block-list"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Plus className="w-4 h-4" /> Adicionar bloqueio
          </CardTitle>
          <CardDescription>
            Bloqueios impedem o gateway de rotear uma classe de tarefa para um provedor específico (anti-alucinação, política).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Select value={taskClass} onValueChange={setTaskClass}>
              <SelectTrigger><SelectValue placeholder="task_class" /></SelectTrigger>
              <SelectContent>
                {TASK_CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger><SelectValue placeholder="provider" /></SelectTrigger>
              <SelectContent>
                {PROVIDERS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              placeholder="Motivo (obrigatório)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="md:col-span-1"
            />
            <Button
              onClick={() => addBlock.mutate()}
              disabled={addBlock.isPending}
            >
              <Plus className="w-4 h-4 mr-1" /> Bloquear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="w-4 h-4" /> Bloqueios ativos ({blocks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : blocks.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum bloqueio configurado.</p>
          ) : (
            <div className="space-y-2">
              {blocks.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-3 border rounded-md p-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline">{b.task_class}</Badge>
                      <span className="text-muted-foreground">×</span>
                      <Badge variant="destructive">{b.provider}</Badge>
                    </div>
                    {b.reason && (
                      <p className="text-xs text-muted-foreground truncate">{b.reason}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeBlock.mutate(b.id)}
                    disabled={removeBlock.isPending}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
